import React, { Component } from 'react';
import './Game_Inventory.css';
import GameAPI from './GameAPI';
import { GameItemTooltip } from './Game_ItemTooltip';
import ItemGrid from '../../ui/ItemGrid';
import ItemSlot from '../../ui/ItemSlot';
import { ItemArtwork, ItemQuantity } from './ItemSlotContent';
import ToastService from '../../services/ToastService';
import QuickBelt from './QuickBelt';
import { isTouchPointer } from './touchItemDrag';
import {
    calculateInventoryLayout,
    INVENTORY_MAX_SLOT_SIZE,
    INVENTORY_SLOT_GAP,
} from './inventoryLayout';

const MAXIMUM_STACK_SIZE = 20;
const splitQuantity = quantity => Math.floor((Number(quantity) || 0) / 2);

function hasValidItemInstanceId(itemInstanceId) {
    return typeof itemInstanceId === 'string' && /^[a-f\d]{24}$/i.test(itemInstanceId);
}

function itemValue(item, camelName, pascalName) {
    return item?.[camelName] ?? item?.[pascalName];
}

export class GameInventory extends Component {
    constructor(props) {
        super(props);
        this.inventorySizeX = 5;
        this.inventorySizeY = 5;
        this.state = {
            itemsLoaded: false,
            table: Array.from({ length: this.inventorySizeY }, () => Array(this.inventorySizeX).fill(null)),
            table2: Array.from({ length: this.inventorySizeY }, () => Array(this.inventorySizeX).fill(null)),
            currentTable: 0,
            tooltip: null,
            layout: {
                columns: this.inventorySizeX,
                rows: this.inventorySizeY,
                slotSize: INVENTORY_MAX_SLOT_SIZE,
            },
        };
        // this.draggedItem = null;
        this.tooltipAnchor = null;
        this.inventoryRef = React.createRef();
        this.layoutObserver = null;
        this.suppressClickUntil = 0;
        this.tooltipPinned = false;
        this.tooltipFocusSuppressed = false;
        this.tooltipFocusSuppressionTimer = null;
        this.equippingItemInstanceId = null;
        this.usingItemKey = null;
        this.learningRecipeItemKey = null;
    }

    componentDidMount() {
        this.lastUserInventory = this.props.userInventory;
        this.loadItems();
        this.populateTable();
        this.observeLayout();
    }

    componentWillUnmount() {
        this.layoutObserver?.disconnect();
        if (this.tooltipFocusSuppressionTimer) {
            window.clearTimeout(this.tooltipFocusSuppressionTimer);
        }
    }

    equipItem = async (item, loadedItem) => {
        if (
            this.props.inventoryActionsDisabled
            || this.equippingItemInstanceId
        ) {
            return;
        }

        const itemDetails = loadedItem
            ?? GameAPI.fetchItemAutoCached(item.itemId, item.itemInstanceId)
            ?? await GameAPI.fetchItemAuto(item.itemId, item.itemInstanceId);
        const itemType = await GameAPI.fetchItemType(itemDetails?.typeId);

        if (!itemType?.isEquipment) {
            return;
        }

        if (!hasValidItemInstanceId(item.itemInstanceId)) {
            ToastService.push('This equipment item cannot be equipped because its instance data is missing.', 'error');
            return;
        }

        this.equippingItemInstanceId = item.itemInstanceId;
        this.suppressTooltipFocusDuringClose();
        this.hideTooltip(true);
        try {
            await GameAPI.equipItem(item.itemInstanceId, item.itemId, item.x, item.y);
        } catch {
            // apiPost already reports the server error through the shared toast host.
        } finally {
            this.equippingItemInstanceId = null;
        }
    };

    activatePrimaryItem = async (item, loadedItem) => {
        if (this.isLearnableRecipe(loadedItem)) {
            await this.learnRecipe(item, loadedItem);
            return;
        }

        const itemType = await GameAPI.fetchItemType(loadedItem?.typeId);
        if (itemType?.isConsumable ?? itemType?.IsConsumable) {
            await this.useItem(item, loadedItem);
        } else if (itemType?.isEquipment ?? itemType?.IsEquipment) {
            await this.equipItem(item, loadedItem);
        }
    };

    useItem = async (item, loadedItem) => {
        if (
            this.props.inventoryActionsDisabled
            || this.usingItemKey
            || !this.props.onUseItem
        ) {
            return;
        }
        this.usingItemKey = `${item.itemId}:${item.x}:${item.y}`;
        this.suppressTooltipFocusDuringClose();
        this.hideTooltip(true);
        try {
            const itemDetails = loadedItem
                ?? GameAPI.fetchItemAutoCached(item.itemId, item.itemInstanceId)
                ?? await GameAPI.fetchItemAuto(item.itemId, item.itemInstanceId);
            await this.props.onUseItem(item, itemDetails);
        } catch {
            // apiPost reports the server-authored error through the shared toast host.
        } finally {
            this.usingItemKey = null;
        }
    };

    learnRecipe = async (item, loadedItem) => {
        if (
            this.props.inventoryActionsDisabled
            || this.learningRecipeItemKey
            || !this.props.onLearnRecipe
        ) {
            return;
        }
        const recipeItemKey = `${item.itemId}:${item.x}:${item.y}`;
        const outputItemName = String(itemValue(
            loadedItem,
            'teachesRecipeForItemName',
            'TeachesRecipeForItemName',
        ) || 'this item');
        this.learningRecipeItemKey = recipeItemKey;
        this.suppressTooltipFocusDuringClose();
        this.hideTooltip(true);
        try {
            const result = await this.props.onLearnRecipe(item, loadedItem);
            const remainingQuantity = Number(
                result?.remainingQuantity ?? result?.RemainingQuantity,
            );
            const remainingText = Number.isFinite(remainingQuantity)
                && remainingQuantity > 0
                ? ` ${remainingQuantity} recipe item${remainingQuantity === 1 ? '' : 's'} ${remainingQuantity === 1 ? 'remains' : 'remain'} in the Pack.`
                : ' The recipe item was consumed.';
            ToastService.push(
                `Recipe for “${outputItemName}” learned.${remainingText}`,
                'success',
            );
        } catch {
            // apiPost reports the server-authored error through the shared toast host.
        } finally {
            this.learningRecipeItemKey = null;
        }
    };

    componentDidUpdate() {
        if (!this.isTooltipSourceCurrent()) {
            this.suppressTooltipFocusDuringClose();
            this.hideTooltip(true);
        }

        if (this.props.userInventory !== this.lastUserInventory) {
            this.lastUserInventory = this.props.userInventory;

            // this.setState({ itemsLoaded: false });
            this.loadItems();
            this.populateTable();
        }
    }

    isInventoryItemCurrent = (source) => {
        if (!source) {
            return false;
        }

        const matchingItem = this.props.userInventory?.find((item) => {
            const sameSlot = Number(item.x) === Number(source.x)
                && Number(item.y) === Number(source.y);
            const sameIdentity = source.itemInstanceId
                ? item.itemInstanceId === source.itemInstanceId
                : item.itemId === source.itemId;
            return sameSlot && sameIdentity;
        });

        return Boolean(
            matchingItem
            && Number(matchingItem.quantity) === Number(source.quantity)
        );
    };

    isTooltipSourceCurrent = () => {
        const source = this.state.tooltip?.item;
        if (!source) {
            return true;
        }

        return this.tooltipAnchor?.isConnected !== false
            && this.isInventoryItemCurrent(source);
    };

    async loadItems() {
        const userInventory = this.props.userInventory;

        if (userInventory) {
            const inventory = userInventory;

            // Filter itemIds where GameAPI.fetchItemCached(item.itemId) is null
            //will hier ein aray aus objecte erstellen mit itemId und itemInstanceId
            const itemAutoIds = inventory.map((item) => ({
                itemId: item.itemId,
                itemInstanceId: item.itemInstanceId
            }));

            await GameAPI.fetchItemsAuto(itemAutoIds);
            const loadedItems = itemAutoIds.map(({ itemId, itemInstanceId }) => (
                GameAPI.fetchItemAutoCached(itemId, itemInstanceId)
            ));
            await GameAPI.fetchRarities(loadedItems.map((item) => item?.rarityId));

            // await Promise.all(itemIds.map((itemId) => GameAPI.fetchItem(itemId)));
            this.setState({ itemsLoaded: true }, this.populateTable);
        }
    }

    populateTable = () => {
        const userInventory = this.props.userInventory;
        let table = null;
        let currentTable = this.state.currentTable;
        if (currentTable === 0) {
            table = this.state.table;
            currentTable = 1;
        } else {
            table = this.state.table2;
            currentTable = 0;
        }

        if (!table || table.length < this.inventorySizeY || table[0].length < this.inventorySizeX) {
            table = Array.from({ length: this.inventorySizeY }, () => Array(this.inventorySizeX).fill(null));
        } else {
            table = table.map((row) => row.map(() => null));
        }

        userInventory?.forEach((item) => {
            const { x, y } = item;
            if (x >= 0 && x < this.inventorySizeX && y >= 0 && y < this.inventorySizeY) {
                // Create a new object to ensure React detects the change
                table[x][y] = { ...item };
            }
        });

        if (currentTable === 0) {
            this.setState({ table: [...table], currentTable });
        }
        else {
            this.setState({ table2: [...table], currentTable });
        }
    };

    onDragStart = (event, item, rowIndex, colIndex) => {
        if (this.props.inventoryActionsDisabled) {
            event.preventDefault();
            return;
        }
        // this.draggedItem = { item, rowIndex, colIndex };
        this.suppressClickUntil = Date.now() + 1000;
        this.hideTooltip(true);
        event.dataTransfer?.setData('text/plain', item.itemInstanceId || item.itemId);
        const half = splitQuantity(item.quantity);
        this.props.onDragStart({
            item,
            rowIndex,
            colIndex,
            fromComponent: "inventory",
            ...(event.shiftKey && half > 0 ? { quantity: half } : {}),
        });
    };

    pickUpHalf = (event, item, rowIndex, colIndex) => {
        event?.stopPropagation?.();
        if (this.props.inventoryActionsDisabled) return;
        const quantity = splitQuantity(item.quantity);
        if (quantity <= 0) return;
        this.hideTooltip(true);
        this.props.onDragStart({
            item,
            rowIndex,
            colIndex,
            fromComponent: 'inventory',
            quantity,
        });
    };

    showTooltipForAnchor = (anchor, item, interactive = false) => {
        if (
            !item
            || !anchor
            || anchor.isConnected === false
            || !this.isInventoryItemCurrent(item)
            || (this.tooltipPinned && !interactive)
        ) {
            return;
        }

        this.tooltipPinned = interactive;
        this.tooltipAnchor = anchor;
        const anchorRect = this.tooltipAnchor?.getBoundingClientRect();
        this.setState({
            tooltip: {
                itemId: item.itemId,
                itemInstanceId: item.itemInstanceId,
                anchorRect,
                interactive,
                item,
            },
        });
    };

    showTooltip = (event, item) => {
        this.showTooltipForAnchor(event.currentTarget, item);
    };

    showTooltipOnFocus = (event, item) => {
        if (this.tooltipFocusSuppressed) {
            return;
        }
        this.showTooltip(event, item);
    };

    suppressTooltipFocusDuringClose = () => {
        this.tooltipFocusSuppressed = true;
        if (this.tooltipFocusSuppressionTimer) {
            window.clearTimeout(this.tooltipFocusSuppressionTimer);
        }
        this.tooltipFocusSuppressionTimer = window.setTimeout(() => {
            this.tooltipFocusSuppressed = false;
            this.tooltipFocusSuppressionTimer = null;
        }, 0);
    };

    hideTooltip = (force = false) => {
        if (this.tooltipPinned && !force) {
            return;
        }

        this.tooltipPinned = false;
        this.tooltipAnchor = null;
        if (this.state.tooltip) {
            this.setState({ tooltip: null });
        }
    };

    getTooltipAnchorRect = () => {
        if (this.tooltipAnchor) {
            return this.tooltipAnchor.getBoundingClientRect();
        }
        return this.state.tooltip?.anchorRect ?? null;
    };

    getTooltipAnchorElement = () => this.tooltipAnchor;

    getEquippedComparison = (itemId, itemInstanceId) => {
        const packItem = GameAPI.fetchItemAutoCached(itemId, itemInstanceId);
        if (!packItem?.typeId) {
            return null;
        }

        return this.props.userEquipment?.find((equipmentItem) => (
            equipmentItem.itemTypeId === packItem.typeId
            && equipmentItem.itemInstanceId !== itemInstanceId
        )) ?? null;
    };

    isLearnableRecipe = (loadedItem) => {
        const recipeOutputItemId = String(itemValue(
            loadedItem,
            'teachesRecipeForItemId',
            'TeachesRecipeForItemId',
        ) || '');
        return Boolean(
            this.props.canLearnRecipes
            && recipeOutputItemId
            && !(this.props.knownRecipeIds || [])
                .some(recipeId => String(recipeId) === recipeOutputItemId)
        );
    };

    getItemActions = (item, loadedItem) => {
        const disabled = Boolean(this.props.inventoryActionsDisabled);
        const disabledReason = this.props.inventoryActionsDisabledReason
            || 'Unavailable during your current action';
        const actions = [];
        if (this.isLearnableRecipe(loadedItem)) {
            actions.push({
                id: 'learn-recipe',
                label: 'Learn recipe',
                disabled,
                disabledReason: disabled ? disabledReason : '',
                onSelect: () => this.learnRecipe(item, loadedItem),
            });
        }
        actions.push({
            id: 'use',
            label: 'Use',
            disabled,
            disabledReason: disabled ? disabledReason : '',
            isAvailable: (_itemDetails, itemType) => (
                Boolean(itemType?.isConsumable ?? itemType?.IsConsumable)
            ),
            onSelect: () => this.useItem(item, loadedItem),
        }, {
            id: 'equip',
            label: 'Equip',
            disabled,
            disabledReason: disabled ? disabledReason : '',
            isAvailable: (_itemDetails, itemType) => (
                Boolean(itemType?.isEquipment ?? itemType?.IsEquipment)
            ),
            onSelect: () => this.equipItem(item, loadedItem),
        }, {
            id: 'move',
            label: 'Move',
            disabled,
            disabledReason: disabled ? disabledReason : '',
            onSelect: () => {
                this.hideTooltip(true);
                this.props.onDragStart({
                    item,
                    rowIndex: Number(item.x),
                    colIndex: Number(item.y),
                    fromComponent: 'inventory',
                });
            },
        });
        if (!item.itemInstanceId && splitQuantity(item.quantity) > 0) {
            actions.push({
                id: 'split',
                label: 'Split half',
                disabled,
                disabledReason: disabled ? disabledReason : '',
                onSelect: (event) => this.pickUpHalf(
                    event,
                    item,
                    Number(item.x),
                    Number(item.y),
                ),
            });
        }
        if (this.props.onRequestDrop) {
            const dropDisabled = disabled || !this.props.canUseGroundLoot;
            actions.push({
                id: 'drop',
                label: 'Drop',
                disabled: dropDisabled,
                disabledReason: dropDisabled
                    ? (disabled ? disabledReason : 'Items cannot be dropped here.')
                    : '',
                onSelect: () => {
                    this.hideTooltip(true);
                    this.props.onRequestDrop(item, loadedItem);
                },
            });
        }
        return actions;
    };

    openItemActions = (event, item) => {
        event.preventDefault();
        this.showTooltipForAnchor(event.currentTarget, item, true);
    };

    handleItemPointerMove = (event, item) => {
        if (!isTouchPointer(event)) {
            this.showTooltip(event, item);
        }
    };

    handleItemPointerUp = (event, item) => {
        if (isTouchPointer(event)) {
            this.suppressClickUntil = Date.now() + 1000;
            if (this.props.draggedItem?.item) {
                const source = this.props.draggedItem;
                if (
                    source.fromComponent === 'inventory'
                    && Number(source.rowIndex) === Number(item.x)
                    && Number(source.colIndex) === Number(item.y)
                ) {
                    this.props.onDrop?.();
                } else {
                    this.onDrop(Number(item.x), Number(item.y));
                }
            } else {
                this.showTooltipForAnchor(event.currentTarget, item, true);
            }
        }
    };

    handleItemPointerLeave = () => {
        this.hideTooltip();
    };

    handleItemClick = (event, item, loadedItem) => {
        if (Date.now() < this.suppressClickUntil) {
            this.suppressClickUntil = 0;
            event.preventDefault();
            return;
        }

        if (this.props.draggedItem?.item) {
            event.stopPropagation();
            this.onDrop(Number(item.x), Number(item.y));
        } else {
            this.activatePrimaryItem(item, loadedItem);
        }
    };

    handleItemKeyDown = (event, item, loadedItem) => {
        if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
            this.openItemActions(event, item);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            this.activatePrimaryItem(item, loadedItem);
            return;
        }
        if (event.key === ' ') {
            event.preventDefault();
            if (this.props.draggedItem?.item) {
                const source = this.props.draggedItem;
                if (
                    source.fromComponent === 'inventory'
                    && Number(source.rowIndex) === Number(item.x)
                    && Number(source.colIndex) === Number(item.y)
                ) {
                    this.props.onDrop?.();
                } else {
                    this.onDrop(Number(item.x), Number(item.y));
                }
            } else {
                this.props.onDragStart({
                    item,
                    rowIndex: Number(item.x),
                    colIndex: Number(item.y),
                    fromComponent: 'inventory',
                });
            }
        }
    };

    onDrop = (rowIndex, colIndex) => {
        if (this.props.inventoryActionsDisabled) {
            this.props.onDrop?.();
            return;
        }
        if (!this.props.draggedItem?.item) return;
        const { item, rowIndex: fromRow, colIndex: fromCol, fromComponent } = this.props.draggedItem;
        const selectedQuantity = Number(this.props.draggedItem.quantity) || 0;
        if (fromComponent === 'quickBelt') {
            this.props.onBeltTransfer?.({
                direction: 'beltToPack',
                itemId: item.itemId,
                sourceSlotIndex: Number(this.props.draggedItem.slotIndex),
                destinationX: rowIndex,
                destinationY: colIndex,
            });
            return;
        }
        if (
            fromComponent === 'inventory'
            && Number(fromRow) === Number(rowIndex)
            && Number(fromCol) === Number(colIndex)
        ) {
            this.props.onDrop();
            return;
        }
        const tableKey = this.state.currentTable === 0 ? 'table' : 'table2';
        const table = this.state[tableKey].map(row => [...row]);
        const targetItem = table[rowIndex][colIndex];
        if (fromComponent === 'inventory') {
            const sourceItem = table[fromRow]?.[fromCol];
            const sourceQuantity = Number(sourceItem?.quantity) || 0;
            const quantity = selectedQuantity > 0
                ? Math.min(selectedQuantity, sourceQuantity)
                : sourceQuantity;
            const compatible = sourceItem
                && targetItem
                && !sourceItem.itemInstanceId
                && !targetItem.itemInstanceId
                && sourceItem.itemId === targetItem.itemId;
            if (sourceItem && compatible) {
                const moved = Math.min(
                    quantity,
                    MAXIMUM_STACK_SIZE - Number(targetItem.quantity),
                );
                if (moved > 0) {
                    table[rowIndex][colIndex] = {
                        ...targetItem,
                        quantity: Number(targetItem.quantity) + moved,
                    };
                    table[fromRow][fromCol] = Number(sourceItem.quantity) === moved
                        ? null
                        : { ...sourceItem, quantity: Number(sourceItem.quantity) - moved };
                }
            } else if (sourceItem && !targetItem) {
                table[rowIndex][colIndex] = {
                    ...sourceItem,
                    quantity,
                    x: rowIndex,
                    y: colIndex,
                };
                table[fromRow][fromCol] = Number(sourceItem.quantity) === quantity
                    ? null
                    : { ...sourceItem, quantity: Number(sourceItem.quantity) - quantity };
            } else if (
                sourceItem
                && targetItem
                && quantity === Number(sourceItem.quantity)
            ) {
                table[fromRow][fromCol] = {
                    ...targetItem,
                    x: fromRow,
                    y: fromCol,
                };
                table[rowIndex][colIndex] = {
                    ...sourceItem,
                    x: rowIndex,
                    y: colIndex,
                };
            }
        } else {
            table[rowIndex][colIndex] = item
                ? { ...item, x: rowIndex, y: colIndex }
                : null;
        }

        this.setState({ [tableKey]: table });
        // this.draggedItem = null;
        this.props.onDrop();
        if (fromRow === -1 && fromCol === -1 && fromComponent === "equipment") { //unequipp to slot#
            GameAPI.unEquipItem(item.itemId, rowIndex, colIndex);
        } else {
            // Call GameAPI to update item positions
            const moveArguments = [
                item.itemInstanceId,
                item.itemId,
                fromRow,
                fromCol,
                rowIndex,
                colIndex,
            ];
            if (selectedQuantity > 0) moveArguments.push(selectedQuantity);
            GameAPI.moveItem(...moveArguments);
        }
    };

    onDragOver = (e) => {
        if (!this.props.inventoryActionsDisabled) {
            e.preventDefault();
        }
    };

    observeLayout = () => {
        const element = this.inventoryRef.current;
        if (!element) return;

        this.updateLayout(element.clientWidth, element.clientHeight);
        if (typeof ResizeObserver === 'undefined') return;

        this.layoutObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            this.updateLayout(entry.contentRect.width, entry.contentRect.height);
        });
        this.layoutObserver.observe(element);
    };

    updateLayout = (width, height) => {
        const element = this.inventoryRef.current;
        const configuredGap = element
            ? Number.parseFloat(getComputedStyle(element).getPropertyValue('--inventory-slot-gap'))
            : NaN;
        const layout = calculateInventoryLayout(width, height, {
            columns: this.inventorySizeX,
            gap: Number.isFinite(configuredGap) ? configuredGap : INVENTORY_SLOT_GAP,
        });

        this.setState((state) => (
            state.layout.columns === layout.columns
            && state.layout.rows === layout.rows
            && state.layout.slotSize === layout.slotSize
                ? null
                : { layout }
        ));
    };

    render() {
        // const { table } = this.state;
        let table;
        if (this.state.currentTable === 0) {
            table = this.state.table;
        }
        else {
            table = this.state.table2;
        }

        const { columns, rows, slotSize } = this.state.layout;
        const equippedComparison = this.state.tooltip
            ? this.getEquippedComparison(
                this.state.tooltip.itemId,
                this.state.tooltip.itemInstanceId,
            )
            : null;

        return (
            <div className="game-inventory">
                <div className="game-inventory__grid-scroll" ref={this.inventoryRef}>
                    <ItemGrid
                        className="inventory-table"
                        columns={columns}
                        gap="var(--inventory-slot-gap)"
                        label="Pack inventory"
                        rows={rows}
                        slotSize={`${slotSize}px`}
                        tabIndex={-1}
                    >
                    {table.flatMap((row, rowIndex) => row.map((item, colIndex) => {
                        const slotIndex = rowIndex * this.inventorySizeX + colIndex;
                        const loadedItem = item && this.state.itemsLoaded
                            ? GameAPI.fetchItemAutoCached(item.itemId, item.itemInstanceId)
                            : null;
                        const rarity = GameAPI.fetchRarityCached(loadedItem?.rarityId);
                        const selected = Boolean(
                            item
                            && this.props.draggedItem?.fromComponent === 'inventory'
                            && Number(this.props.draggedItem.rowIndex) === rowIndex
                            && Number(this.props.draggedItem.colIndex) === colIndex
                        );
                        return (
                            <ItemSlot
                                aria-colindex={(slotIndex % columns) + 1}
                                aria-label={`Pack slot ${slotIndex + 1}`}
                                aria-rowindex={Math.floor(slotIndex / columns) + 1}
                                aria-selected={selected || undefined}
                                className={`inventory-cell${
                                    this.props.draggedItem?.item
                                    && !this.props.inventoryActionsDisabled
                                        ? ' inventory-cell--compatible'
                                        : ''
                                }`}
                                item={loadedItem}
                                itemType={GameAPI.fetchItemTypeCached(loadedItem?.typeId)}
                                key={`${rowIndex}-${colIndex}`}
                                rarity={rarity?.name?.toLowerCase()}
                                rarityColor={rarity?.colorHex}
                                role="gridcell"
                                selected={selected}
                                onDragOver={this.onDragOver}
                                onDrop={() => this.onDrop(rowIndex, colIndex)}
                                onClick={() => {
                                    if (this.props.draggedItem?.item) {
                                        this.onDrop(rowIndex, colIndex);
                                    }
                                }}
                                onKeyDown={(event) => {
                                    if (
                                        !item
                                        && this.props.draggedItem?.item
                                        && (event.key === 'Enter' || event.key === ' ')
                                    ) {
                                        event.preventDefault();
                                        this.onDrop(rowIndex, colIndex);
                                    }
                                }}
                                tabIndex={!item && this.props.draggedItem?.item ? 0 : -1}
                            >
                                {item && (
                                    <div
                                        className="inventory-item"
                                        onMouseEnter={(event) => this.showTooltip(event, item)}
                                        onMouseLeave={() => this.hideTooltip()}
                                        onPointerLeave={this.handleItemPointerLeave}
                                    >
                                        <button
                                            aria-expanded={Boolean(
                                                this.state.tooltip?.interactive
                                                && this.state.tooltip.itemInstanceId === item.itemInstanceId
                                                && this.state.tooltip.itemId === item.itemId
                                                && Number(this.state.tooltip.item?.x) === rowIndex
                                                && Number(this.state.tooltip.item?.y) === colIndex
                                            )}
                                            aria-haspopup="menu"
                                            aria-disabled={
                                                this.props.inventoryActionsDisabled
                                                || undefined
                                            }
                                            aria-label={`${loadedItem?.name || 'Pack item'}, quantity ${item.quantity}`}
                                            className="inventory-item__main"
                                            draggable={!this.props.inventoryActionsDisabled}
                                            onClick={(event) => this.handleItemClick(event, item, loadedItem)}
                                            onContextMenu={(event) => this.openItemActions(event, item)}
                                            onDragEnd={this.props.onDragEnd}
                                            onDragStart={(event) => this.onDragStart(event, item, rowIndex, colIndex)}
                                            onFocus={(event) => this.showTooltipOnFocus(event, item)}
                                            onBlur={() => this.hideTooltip()}
                                            onKeyDown={(event) => this.handleItemKeyDown(event, item, loadedItem)}
                                            onPointerMove={(event) => this.handleItemPointerMove(event, item)}
                                            onPointerUp={(event) => this.handleItemPointerUp(event, item)}
                                            title={Number(item.quantity) > 1
                                                ? 'Drag to move; Shift-drag moves half'
                                                : undefined}
                                            type="button"
                                        >
                                            <ItemArtwork
                                                className="equippedFrame"
                                                item={loadedItem}
                                                itemId={item.itemId}
                                            />
                                        </button>
                                        {splitQuantity(item.quantity) > 0 && !item.itemInstanceId ? (
                                            <ItemQuantity
                                                ariaLabel={`Pick up half of ${loadedItem?.name || 'Pack item'}, ${splitQuantity(item.quantity)} of ${item.quantity}`}
                                                disabled={this.props.inventoryActionsDisabled}
                                                onClick={event => this.pickUpHalf(
                                                    event,
                                                    item,
                                                    rowIndex,
                                                    colIndex,
                                                )}
                                                quantity={item.quantity}
                                                title="Pick up half this stack"
                                            />
                                        ) : (
                                            <ItemQuantity quantity={item.quantity} />
                                        )}
                                    </div>
                                )}
                            </ItemSlot>
                        );
                    }))}
                    </ItemGrid>
                </div>
                <QuickBelt
                    capacity={this.props.quickBeltCapacity}
                    disabled={this.props.inventoryActionsDisabled}
                    draggedItem={this.props.draggedItem}
                    entries={this.props.quickBelt}
                    management
                    onDragEnd={this.props.onDragEnd}
                    onDragStart={this.props.onDragStart}
                    onTransfer={this.props.onBeltTransfer}
                    onUse={(entry, itemDetails) => this.props.onUseItem?.({
                        ...entry,
                        source: 'belt',
                    }, itemDetails)}
                />
                {this.state.tooltip && (
                    <GameItemTooltip
                        itemId={this.state.tooltip.itemId}
                        itemInstanceId={this.state.tooltip.itemInstanceId}
                        anchorRect={this.state.tooltip.anchorRect}
                        getAnchorRect={this.getTooltipAnchorRect}
                        getAnchorElement={this.getTooltipAnchorElement}
                        interactive={this.state.tooltip.interactive}
                        actions={this.getItemActions(
                            this.state.tooltip.item,
                            GameAPI.fetchItemAutoCached(
                                this.state.tooltip.itemId,
                                this.state.tooltip.itemInstanceId,
                            ),
                        )}
                        comparisonItemId={equippedComparison?.itemId}
                        comparisonItemInstanceId={equippedComparison?.itemInstanceId}
                        comparisonLabel="Equipped"
                        onRequestClose={() => this.hideTooltip(true)}
                    />
                )}
            </div>
        );
    }
}
